import { useEffect, useRef, useState, useCallback, useMemo } from 'react'

const ASSIGNMENT_TYPES = ['Essay', 'Document', 'LoR']

const keyM = id => `m:${id}`
const keyU = id => `u:${id}`

const listSignature = (
	resetKey,
	lockedSelection,
	assignmentType,
	missingItems,
	uploadedItems,
) =>
	`${resetKey}|${lockedSelection ? 1 : 0}|${assignmentType || ''}|${(missingItems || [])
		.map(i => i?.id)
		.join(',')}|${(uploadedItems || []).map(i => i?.id).join(',')}`

export const useStepTypeApplications = ({
	onSelect,
	assignmentType = '',
	missingItems = [],
	uploadedItems = [],
	onDocumentTargetsChange = () => {},
	lockedSelection = false,
	resetKey = 0,
}) => {
	const [open, setOpen] = useState(false)
	const dropdownRef = useRef(null)
	const listSigRef = useRef('')
	const [selectedKeys, setSelectedKeys] = useState(() => new Set())

	const sig = useMemo(
		() =>
			listSignature(
				resetKey,
				lockedSelection,
				assignmentType,
				missingItems,
				uploadedItems,
			),
		[resetKey, lockedSelection, assignmentType, missingItems, uploadedItems],
	)

	useEffect(() => {
		if (!open) return
		const onDown = e => {
			if (!dropdownRef.current?.contains(e.target)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', onDown)
		return () => document.removeEventListener('mousedown', onDown)
	}, [open])

	const toggleDropdown = useCallback(() => {
		setOpen(prev => !prev)
	}, [])

	const selectAssignmentType = useCallback(
		type => {
			onSelect?.(type)
			setOpen(false)
		},
		[onSelect],
	)

	useEffect(() => {
		if (lockedSelection && missingItems?.length === 1 && !uploadedItems?.length) {
			setSelectedKeys(new Set([keyM(missingItems[0].id)]))
			listSigRef.current = sig
			return
		}
		if (
			lockedSelection &&
			uploadedItems?.length === 1 &&
			!(missingItems || []).length
		) {
			setSelectedKeys(new Set([keyU(uploadedItems[0].id)]))
			listSigRef.current = sig
			return
		}

		if (listSigRef.current === sig) return
		listSigRef.current = sig

		const next = new Set()
		;(missingItems || []).forEach(item => {
			if (item?.id) next.add(keyM(item.id))
		})
		setSelectedKeys(next)
	}, [sig, lockedSelection, missingItems, uploadedItems])

	const emitTargets = useCallback(
		keys => {
			const targets = []
			;(missingItems || []).forEach(item => {
				if (!item?.id) return
				if (keys.has(keyM(item.id))) {
					targets.push({
						requirementId: item.requirementId || item.id,
						action: item.action || 'upload_new',
						title: item.title,
					})
				}
			})
			;(uploadedItems || []).forEach(item => {
				if (!item?.id) return
				if (keys.has(keyU(item.id))) {
					targets.push({
						requirementId: item.requirementId || item.id,
						action: item.action || 'reupload',
						title: item.title,
					})
				}
			})
			onDocumentTargetsChange(targets)
		},
		[missingItems, uploadedItems, onDocumentTargetsChange],
	)

	useEffect(() => {
		emitTargets(selectedKeys)
	}, [selectedKeys, emitTargets])

	const onToggleSelectedAll = useCallback(() => {
		if (lockedSelection) return
		const allMissing = (missingItems || []).map(item => keyM(item.id)).filter(Boolean)
		setSelectedKeys(prev => {
			const allOn = allMissing.every(k => prev.has(k))
			if (allOn) return new Set()
			return new Set(allMissing)
		})
	}, [missingItems, lockedSelection])

	const toggleKey = useCallback(
		k => {
			if (lockedSelection) return
			setSelectedKeys(prev => {
				const next = new Set(prev)
				if (next.has(k)) next.delete(k)
				else next.add(k)
				return next
			})
		},
		[lockedSelection],
	)

	const missingIds = (missingItems || []).map(item => keyM(item.id)).filter(Boolean)
	const isSelectedAll =
		missingIds.length > 0 && missingIds.every(k => selectedKeys.has(k))

	return {
		open,
		dropdownRef,
		assignmentTypes: ASSIGNMENT_TYPES,
		toggleDropdown,
		selectAssignmentType,
		isSelectedAll,
		onToggleSelectedAll,
		toggleKey,
		selectedKeys,
		keyM,
		keyU,
		lockedSelection,
	}
}
